import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';

/**
 * Service for maintaining backward compatibility with legacy customization patterns.
 *
 * This service enables shops that used legacy `customization.afterRender` hooks
 * in TAE configuration to continue working without code changes. It executes
 * both general and block-specific afterRender callbacks after widget rendering.
 *
 * @remarks
 * Supports two levels of customization:
 * 1. General: `customization.afterRender(block)` - applies to all blocks
 * 2. Block-specific: `customization[blockType].afterRender(block)` - applies to specific block types
 *
 * @example
 * Configure legacy hooks in TAE config:
 * ```javascript
 * window.boostSDAppConfig.customization = {
 *   // General hook for all blocks
 *   afterRender: function(block) {
 *     console.log('Block rendered:', block.id);
 *   },
 *   // Block-type specific hooks
 *   recommendation: {
 *     afterRender: function(block) {
 *       // Custom logic for recommendation widgets
 *       jQuery('.boost-pfs-recommendation').customPlugin();
 *     }
 *   }
 * };
 * ```
 */
@Injectable
export class CustomizationCompatibility {
  constructor(protected appService: AppService) {}

  /**
   * Executes legacy afterRender customization hooks for a rendered block.
   *
   * Invokes both general and block-type-specific afterRender callbacks if configured.
   * The general hook runs first, followed by the type-specific hook. Both are optional.
   *
   * @param blockId - The unique identifier of the rendered block (e.g., 'boost-sd-widget-homepage-1')
   * @param blockType - The type of block rendered (e.g., 'recommendation', 'search', 'filter')
   *
   * @remarks
   * This method is called automatically after widget rendering and should not be
   * called directly unless implementing custom rendering logic. Hooks are executed
   * synchronously in the order: general → type-specific.
   *
   * @example
   * Usage in custom controller:
   * ```typescript
   * protected afterWidgetRender() {
   *   super.afterWidgetRender();
   *   const blockId = this.props?.value.widgetId;
   *   if (blockId) {
   *     this.customizationCompatibility.afterRender(blockId, 'recommendation');
   *   }
   * }
   * ```
   */
  afterRender(blockId: string, blockType: string) {
    const { customization = {} } = this.appService.TAEAppConfig;
    const block = this.appService.TAEApp.blocks[blockId];

    // Guard against undefined block
    if (!block) {
      return;
    }

    const generalAfterRender = Reflect.get(customization, 'afterRender');

    if (typeof generalAfterRender === 'function') {
      generalAfterRender(block);
    }

    const blockAfterRender = customization[blockType]?.afterRender;
    if (typeof blockAfterRender === 'function') {
      blockAfterRender(block);
    }
  }
}
